import type { Pinia } from 'pinia'
import type { Router } from 'vue-router'
import { configureAuthentication } from './recovery'
import {
  applyAuthSessionPayload, clearAuthSession, getAuthSessionPayload, getAnonymousSessionClaimTarget,
  saveAnonymousSessionClaimTarget, clearAnonymousSessionClaimTarget, saveReturnTo,
  saveRedirectChatState, consumeRedirectChatState,
} from './session'
import { useConversationStore } from '@/stores/conversationStore'
import { useChatHistoryStore } from '@/stores/chatHistoryStore'
import { useFileUploadStore } from '@/stores/fileUploadStore'
import { ApiError, chatClient, clearBotApiCache } from '@/services'
import { isAbortError } from '@/lib/requestCancellation'

/** Application composition root: auth owns ordering; stores own their data. */
export function configureChatAuthentication(pinia: Pinia, router: Router) {
  const conversation = useConversationStore(pinia)
  const history = useChatHistoryStore(pinia)
  const files = useFileUploadStore(pinia)

  configureAuthentication({
    getSession: getAuthSessionPayload,
    installSession: applyAuthSessionPayload,
    prepareRedirect(previousSession) {
      if (!conversation.activeBotName) return
      if (!previousSession) saveAnonymousSessionClaimTarget(conversation.getActiveAnonymousSessionClaimTarget())
      saveRedirectChatState({
        user: previousSession?.user ?? null,
        botName: conversation.activeBotName,
        sessionId: conversation.activeConversationId,
        draft: conversation.composerDraft?.content ?? '',
      })
    },
    suspend(previousSession) {
      if (!previousSession) saveAnonymousSessionClaimTarget(conversation.getActiveAnonymousSessionClaimTarget())
      conversation.suspendForAuthentication()
      history.suspendForAuthentication()
      clearAuthSession()
    },
    clearUserData() {
      const botName = conversation.activeBotName
      conversation.clearAllState()
      conversation.setActiveBotName(botName)
      history.clearSessions()
      files.clear()
      clearBotApiCache()
      clearAnonymousSessionClaimTarget()
    },
    async reconcile(payload, signal) {
      // LTI views load the server-selected launch session through their launch flow.
      if (payload.authSource !== 'cas') return
      if (router.currentRoute.value.name === 'auth-callback') {
        const context = consumeRedirectChatState()
        if (context && (!context.user || (context.user.sub === payload.user.sub && context.user.org_id === payload.user.org_id))) {
          conversation.setActiveBotName(context.botName)
          conversation.activeConversationId = context.sessionId
          conversation.composerDraft = { content: context.draft, requestedAt: Date.now() }
        }
      }
      let notice: string | undefined
      const claimTarget = getAnonymousSessionClaimTarget()
      if (claimTarget) {
        try {
          await conversation.claimAnonymousSession(claimTarget, signal)
          if (router.currentRoute.value.name === 'auth-callback') {
            saveReturnTo(router.resolve({ name: 'bot-session', params: { botId: claimTarget.botId, sessionId: claimTarget.sessionId } }).fullPath)
          }
        } catch (error) {
          if (signal.aborted || isAbortError(error) || (error instanceof ApiError && error.status === 401)) throw error
          // Authentication succeeded; optional conversation restoration did not.
          // Keep the local transcript, but never send under unverified ownership.
          conversation.retainUnclaimedConversation()
          notice = 'auth.conversationNotRestored'
        }
        clearAnonymousSessionClaimTarget()
      }
      const botName = conversation.activeBotName
      if (!botName) return notice ? { notice } : undefined
      try {
        await conversation.fetchBot(botName, signal)
      } catch (error) {
        // A successful login may still be denied access. The bot view owns 403.
        if (error instanceof ApiError && error.status === 403) return
        throw error
      }
      signal.throwIfAborted()
      let sessionId = conversation.activeConversationId
      if (!notice && !sessionId && router.currentRoute.value.meta.chatSurface === 'helper_embed') {
        sessionId = (await chatClient.getMostRecentSession(conversation.resolveBotId(botName), signal))?.id ?? null
      }
      if (!notice && sessionId && !sessionId.startsWith('pending-')) {
        await conversation.loadConversation(sessionId, botName, signal)
      }
      signal.throwIfAborted()
      if (router.currentRoute.value.meta.chatSurface === 'desktop_workspace') await history.loadSessions()
      return notice ? { notice } : undefined
    },
  })
}
